import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { hasTier } from '@/lib/tiers';
import { Dumbbell, Salad, Camera, PlayCircle, TrendingUp, MessageSquare, CalendarCheck, ClipboardCheck, Trophy, Sparkles, ArrowRight, Lock } from 'lucide-react';

const CONFIG = {
  regen_plus: {
    color: '#00C8FF', name: 'YourReGen+', icon: Sparkles, target: '/choose-journey', cta: 'Upgrade Now',
    note: 'Available on the App Store & Google Play',
    benefits: [
      { icon: Dumbbell, label: 'Personalized Workout Programs', desc: 'Workouts tailored to your goals.' },
      { icon: Salad, label: 'Personalized Nutrition Plans', desc: 'Meal plans and macros built for you.' },
      { icon: Camera, label: 'Scan Meals & Track Nutrition', desc: 'Snap a photo to log food and macros instantly.' },
      { icon: PlayCircle, label: 'Complete Exercise Video Library', desc: 'Video demos for every movement.' },
      { icon: TrendingUp, label: 'Premium Analytics & YourReGen Score', desc: 'Track your progress with advanced insights.' },
    ],
  },
  regen_method: {
    color: '#65A30D', name: 'YourReGen Protocol', icon: Trophy, target: '/enroll/regen-method', cta: 'Work with Coach JP',
    note: 'Personalized coaching with Coach JP',
    benefits: [
      { icon: MessageSquare, label: 'Direct Coach JP Support', desc: '1:1 messaging & accountability.' },
      { icon: CalendarCheck, label: 'Weekly Check-ins', desc: 'Reviews of your progress & photos.' },
      { icon: ClipboardCheck, label: 'Coach-Adjusted Programs', desc: 'Your plan updated as you progress.' },
      { icon: Trophy, label: 'Full Transformation', desc: '6 or 12-month personalized journey.' },
    ],
  },
};

export function UpgradeWall({ needed = 'regen_plus', feature }) {
  const nav = useNavigate();
  const c = CONFIG[needed];
  return (
    <div className="flex flex-col items-center text-center px-2 py-6" data-testid="upgrade-wall">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 relative" style={{ background: `${c.color}18` }}>
        <c.icon className="w-8 h-8" style={{ color: c.color }} />
        <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[var(--c-elev2)] border border-[var(--c-hair)] flex items-center justify-center"><Lock className="w-3.5 h-3.5 text-[var(--c-ink-soft)]" /></span>
      </div>
      <div className="label-mini" style={{ color: c.color }}>{feature ? `${feature} is a ${c.name} feature` : `Unlock ${c.name}`}</div>
      <h1 className="font-display font-black text-3xl mt-1 tracking-tight">Unlock {c.name}</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-xs">Upgrade to access:</p>

      <div className="w-full max-w-sm mt-5 space-y-2.5">
        {c.benefits.map(b => (
          <div key={b.label} className="flex items-center gap-3 text-left rounded-2xl border border-[var(--c-hair)] bg-[var(--c-film)] p-3.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${c.color}1a` }}>
              <b.icon className="w-5 h-5" style={{ color: c.color }} />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm">{b.label}</div>
              <div className="text-xs text-muted-foreground">{b.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => nav(c.target)} data-testid="upgrade-wall-cta"
              className="w-full max-w-sm h-13 mt-6 rounded-2xl font-black text-[#04203f] flex items-center justify-center gap-2 py-4 active:scale-[0.99] transition"
              style={{ background: c.color }}>
        {c.cta} <ArrowRight className="w-5 h-5" />
      </button>
      <p className="text-[11px] text-muted-foreground mt-2">{c.note}</p>
    </div>
  );
}

// Route guard: renders children only if user's tier meets `needed`, else an UpgradeWall.
export function TierGate({ needed = 'regen_plus', feature, children }) {
  const { user } = useAuth();
  if (hasTier(user?.subscription_tier, needed)) return children;
  return <UpgradeWall needed={needed} feature={feature} />;
}
